import { Ban, MoreHorizontal, PauseCircle, User } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import {
  type Column,
  DataTable,
  type FilterDef,
  type TableState,
} from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'

import { AvatarCell } from '@/components/shared'
import { count, minutesAgo } from '@/lib'
import type { Player, PlayerStatus } from '@/models'

const STATUS_VARIANT: Record<PlayerStatus, 'success' | 'warning' | 'danger'> = {
  Active: 'success',
  Suspended: 'warning',
  Banned: 'danger',
}

export const PLAYER_COLUMNS: Column<Player>[] = [
  {
    key: 'id',
    header: 'Player ID',
    width: '130px',
    cell: (row) => <span className="text-fg-subtle text-sm">{row.id}</span>,
  },
  {
    key: 'username',
    header: 'Username',
    width: 'minmax(180px,1.4fr)',
    sortable: true,
    cell: (row) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <AvatarCell name={row.username} />
        <span className="text-fg truncate font-medium">{row.username}</span>
      </span>
    ),
  },
  { key: 'level', header: 'Level', width: '70px', align: 'end', sortable: true },
  {
    key: 'trophies',
    header: 'Trophies',
    width: '110px',
    align: 'end',
    sortable: true,
    cell: (row) => <span className="text-fg font-medium tabular-nums">{count(row.trophies)}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    width: '110px',
    cell: (row) => (
      <Badge dot variant={STATUS_VARIANT[row.status]}>
        {row.status}
      </Badge>
    ),
  },
  { key: 'country', header: 'Country', width: '90px' },
  {
    key: 'lastSeenMin',
    header: 'Last seen',
    width: '110px',
    align: 'end',
    sortable: true,
    cell: (row) => <span className="text-fg-muted">{minutesAgo(row.lastSeenMin)}</span>,
  },
]

export const PLAYER_FILTERS: FilterDef[] = [
  {
    key: 'status',
    label: 'All statuses',
    options: [
      { value: '', label: 'All statuses' },
      { value: 'Active', label: 'Active' },
      { value: 'Suspended', label: 'Suspended' },
      { value: 'Banned', label: 'Banned' },
    ],
  },
  {
    key: 'country',
    label: 'All countries',
    options: [
      { value: '', label: 'All countries' },
      ...['US', 'KR', 'DE', 'BR', 'JP', 'FR', 'GB', 'MX', 'IN', 'SE', 'TR', 'CA'].map((code) => ({
        value: code,
        label: code,
      })),
    ],
  },
]

interface PlayersTableProps {
  rows: readonly Player[]
  total: number
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  onOpen: (player: Player) => void
  empty?: React.ReactNode
  onSuspend: (player: Player) => void
  onBan: (player: Player) => void
}

export function PlayersTable({
  rows,
  total,
  state,
  onStateChange,
  onOpen,
  onSuspend,
  onBan,
  empty,
}: PlayersTableProps) {
  return (
    <DataTable
      columns={PLAYER_COLUMNS}
      rows={rows}
      total={total}
      state={state}
      onStateChange={onStateChange}
      rowKey={(row) => row.id}
      onRowClick={onOpen}
      empty={empty}
      rowActions={(row) => (
        <DropdownMenu
          trigger={
            <Button variant="ghost" size="icon" aria-label={`Actions for ${row.username}`}>
              <MoreHorizontal />
            </Button>
          }
        >
          <DropdownMenuItem onClick={() => onOpen(row)}>
            <User />
            View Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onSuspend(row)}>
            <PauseCircle />
            {row.status === 'Suspended' ? 'Lift suspension' : 'Suspend'}
          </DropdownMenuItem>
          <DropdownMenuItem destructive onClick={() => onBan(row)}>
            <Ban />
            {row.status === 'Banned' ? 'Unban' : 'Ban player'}
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    />
  )
}
